/**
 * Me Today — global theme scripts.
 * Vanilla custom elements, no build step, loaded with `defer` on every page.
 * Product-specific logic (variant picker, gallery, sticky bar) lives in product.js.
 */

const themeConfig = window.theme || {};
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Utilities ----------------------------------------------------------------- */

function debounce(fn, wait = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

async function fetchJSON(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { Accept: 'application/json', ...(options.headers || {}) },
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || themeConfig.strings?.cartError);
  }
  return data;
}

/** Shopify money formatting, mirroring shop.money_format. */
function formatMoney(cents, format = themeConfig.settings?.moneyFormat || '${{amount}}') {
  const value = Number(cents) || 0;
  const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
    const [whole, fraction] = (number / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0);
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(value, 2, "'", '.');
      case 'amount_with_space_separator':
        return withDelimiters(value, 2, ' ', ',');
      default:
        return withDelimiters(value, 2);
    }
  });
}

function announce(message) {
  const region = document.getElementById('a11y-live-region');
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => (region.textContent = message));
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.hidden = count === 0;
    const visible = el.querySelector('[aria-hidden]');
    if (visible) visible.textContent = count;
  });
}

/* Cart: one place that talks to the Ajax API and re-renders cart sections --- */

const Cart = {
  /** Section ids currently on the page that render cart contents. */
  sectionIds() {
    return [...document.querySelectorAll('cart-drawer, cart-items')].map((el) => el.dataset.sectionId).filter(Boolean);
  },

  async add(formData) {
    const sections = this.sectionIds();
    if (sections.length) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    const item = await fetchJSON(`${themeConfig.routes.cartAdd}.js`, { method: 'POST', body: formData });
    if (item.sections && Object.values(item.sections).some(Boolean)) this.render(item.sections);
    else await this.refresh();
    return item;
  },

  /** Re-render cart sections from the server (Section Rendering API). */
  async refresh() {
    const ids = this.sectionIds();
    if (!ids.length) return;
    const response = await fetch(`${themeConfig.routes.root}?sections=${ids.join(',')}`);
    if (response.ok) this.render(await response.json());
  },

  async change(line, quantity) {
    const cart = await fetchJSON(`${themeConfig.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
    });
    this.render(cart.sections);
    updateCartCount(cart.item_count);
    return cart;
  },

  async updateNote(note) {
    return fetchJSON(`${themeConfig.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
  },

  render(sections) {
    if (!sections) return;
    document.querySelectorAll('cart-drawer, cart-items').forEach((el) => {
      const html = sections[el.dataset.sectionId];
      if (html) el.renderFrom(html);
    });
  },
};

window.themeUtils = { debounce, fetchJSON, formatMoney, announce, updateCartCount, Cart };

/* Header -------------------------------------------------------------------- */

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-menu-drawer]');
    this.setHeight = this.setHeight.bind(this);
    this.setHeight();
    window.addEventListener('resize', debounce(this.setHeight, 150));

    // Compact state on scroll.
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.classList.toggle('is-scrolled', window.scrollY > 40);
          ticking = false;
        });
      },
      { passive: true }
    );

    // Close open <details> on outside click / Escape.
    document.addEventListener('click', (event) => {
      this.querySelectorAll('details[open]').forEach((details) => {
        if (!details.contains(event.target)) this.closeDetails(details);
      });
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = event.target.closest('details[open]');
      if (!open) return;
      this.closeDetails(open);
      open.querySelector('summary')?.focus();
    });

    this.querySelectorAll('[data-dropdown]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.querySelectorAll('[data-dropdown][open]').forEach((other) => other !== details && (other.open = false));
      });
    });

    this.drawer?.addEventListener('toggle', () => {
      const bottom = this.getBoundingClientRect().bottom;
      this.style.setProperty('--drawer-top', `${Math.max(bottom, 0)}px`);
      document.body.classList.toggle('is-locked', this.drawer.open);
    });
    window.matchMedia('(min-width: 990px)').addEventListener('change', () => this.drawer && this.closeDetails(this.drawer));
  }

  closeDetails(details) {
    details.open = false;
    if (details === this.drawer) document.body.classList.remove('is-locked');
  }

  setHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }
}
customElements.define('sticky-header', StickyHeader);

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.children];
    if (this.messages.length < 2 || reducedMotion.matches) return;
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 5000;
    this.start();
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stop() {
    clearInterval(this.timer);
  }

  next() {
    this.messages[this.index].classList.remove('is-active');
    this.index = (this.index + 1) % this.messages.length;
    this.messages[this.index].classList.add('is-active');
  }
}
customElements.define('announcement-rotator', AnnouncementRotator);

/* Quantity input ------------------------------------------------------------ */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const previous = this.input.value;
        button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
  }
}
customElements.define('quantity-input', QuantityInput);

/* Product form (add to cart) ------------------------------------------------ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-error]');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  get submitButtons() {
    // The main button plus any sticky bar button bound to this form via form="".
    // Note: read the id attribute; `form.id` is clobbered by the form's <input name="id">.
    const formId = CSS.escape(this.form.getAttribute('id'));
    return [...document.querySelectorAll(`[type="submit"][form="${formId}"], #${formId} [type="submit"][name="add"]`)];
  }

  async onSubmit(event) {
    event.preventDefault();
    const buttons = this.submitButtons;
    if (buttons.some((b) => b.classList.contains('is-loading')) || buttons[0]?.disabled) return;

    this.setError();
    buttons.forEach((b) => {
      b.classList.add('is-loading');
      b.setAttribute('aria-disabled', 'true');
    });

    try {
      if (themeConfig.settings?.cartType === 'page' || !document.querySelector('cart-drawer')) {
        await fetchJSON(`${themeConfig.routes.cartAdd}.js`, { method: 'POST', body: new FormData(this.form) });
        window.location = themeConfig.routes.cart;
        return;
      }
      const item = await Cart.add(new FormData(this.form));
      announce(`${item.product_title}. ${themeConfig.strings?.addedToCart || ''}`);
      document.querySelector('cart-drawer')?.open(event.submitter || buttons[0]);
    } catch (error) {
      this.setError(error.message);
    } finally {
      buttons.forEach((b) => {
        b.classList.remove('is-loading');
        b.removeAttribute('aria-disabled');
      });
    }
  }

  setError(message) {
    if (!this.error) return;
    this.error.hidden = !message;
    this.error.textContent = message || '';
  }
}
customElements.define('product-form', ProductForm);

/* Shared behaviour for anything that lists cart lines ---------------------- */

const CartLinesMixin = (Base) =>
  class extends Base {
    bindCartEvents() {
      const onQuantity = debounce((input) => this.updateLine(input.dataset.line, Number(input.value)), 350);
      const onNote = debounce((textarea) => Cart.updateNote(textarea.value).catch(console.error), 500);

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-line]')) onQuantity(event.target);
      });
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-cart-note]')) onNote(event.target);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (!remove) return;
        event.preventDefault();
        this.updateLine(remove.dataset.removeLine, 0);
      });
    }

    async updateLine(line, quantity) {
      this.classList.add('is-loading');
      const focusedId = document.activeElement?.id;
      try {
        await Cart.change(line, quantity);
        const refocus = focusedId && document.getElementById(focusedId);
        if (refocus) refocus.focus();
        else this.querySelector('[data-cart-drawer-close], h1, h2')?.focus?.();
      } catch (error) {
        const errorEl = this.querySelector('[data-cart-error]');
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = error.message;
        }
      } finally {
        this.classList.remove('is-loading');
      }
    }
  };

/* Cart drawer --------------------------------------------------------------- */

class CartDrawer extends CartLinesMixin(HTMLElement) {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.bindCartEvents();

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-drawer-toggle]');
      if (!toggle || document.body.classList.contains('template-cart')) return;
      event.preventDefault();
      this.open(toggle);
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) this.close();
      // Click on the backdrop (the dialog element itself, outside its content).
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
  }

  open(opener) {
    if (this.dialog.open) return;
    this.opener = opener || document.activeElement;
    this.dialog.showModal();
    document.body.classList.add('is-locked');
    requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
    this.dialog.querySelector('[data-cart-drawer-close]')?.focus();
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.classList.remove('is-open');
    const finish = () => {
      this.dialog.close();
      document.body.classList.remove('is-locked');
      this.opener?.focus?.();
    };
    reducedMotion.matches ? finish() : setTimeout(finish, 380);
  }

  renderFrom(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-drawer');
    if (!fresh) return;
    this.dialog.innerHTML = fresh.querySelector('dialog').innerHTML;
    const count = Number(fresh.dataset.itemCount);
    this.dataset.itemCount = count;
    updateCartCount(count);
  }
}
customElements.define('cart-drawer', CartDrawer);

/* Cart page ----------------------------------------------------------------- */

class CartItems extends CartLinesMixin(HTMLElement) {
  connectedCallback() {
    this.bindCartEvents();
  }

  renderFrom(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-items');
    if (fresh) this.innerHTML = fresh.innerHTML;
  }
}
customElements.define('cart-items', CartItems);

/* Carousel (reviews, look inside, video stories) ---------------------------- */

class SliderCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.status = this.querySelector('[data-status]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(1);
      if (event.key === 'ArrowLeft') this.go(-1);
    });
    window.addEventListener('resize', debounce(() => this.update(), 150));
    this.update();
  }

  get step() {
    const slide = this.slides[0];
    if (!slide) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  go(direction) {
    this.track.scrollBy({ left: direction * this.step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max - 2;
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
    this.toggleAttribute('data-static', max <= 2);
    if (this.status && this.step) {
      const visible = Math.max(1, Math.round(this.track.clientWidth / this.step));
      const current = atEnd ? this.slides.length : Math.min(this.slides.length, Math.round(this.track.scrollLeft / this.step) + 1);
      const template = themeConfig.strings?.slideStatus || '[current] / [total]';
      this.status.textContent = template
        .replace('[current]', visible > 1 ? `${current}–${Math.min(current + visible - 1, this.slides.length)}` : current)
        .replace('[total]', this.slides.length);
    }
  }
}
customElements.define('slider-carousel', SliderCarousel);

/* Deferred video (click to load) ------------------------------------------- */

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => this.load());
  }

  load() {
    const template = this.querySelector('template');
    if (!template || this.loaded) return;
    this.loaded = true;
    const content = template.content.firstElementChild.cloneNode(true);
    this.querySelector('.media').replaceChildren(content);
    if (content.tagName === 'VIDEO') content.play?.();
    content.focus?.();
  }
}
customElements.define('deferred-media', DeferredMedia);

/* Hero books: scroll progress drives the fan-out of the second cover ------- */

class HeroBooks extends HTMLElement {
  connectedCallback() {
    if (!this.classList.contains('hero__books--pair') || reducedMotion.matches) return;
    this.section = this.closest('section') || this;
    this.stage = this.closest('.hero__stage') || this;
    let ticking = false;
    this.onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const rect = this.section.getBoundingClientRect();
        // 0 while the hero sits at the top, 1 once it has scrolled ~55% of its height.
        const progress = Math.min(1, Math.max(0, -rect.top / (rect.height * 0.55)));
        this.stage.style.setProperty('--p', progress.toFixed(3));
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }
}
customElements.define('hero-books', HeroBooks);

/* Scroll statement: words brighten as the section scrolls through view ------ */

class ScrollWords extends HTMLElement {
  connectedCallback() {
    if (reducedMotion.matches) return;
    this.words = [...this.querySelectorAll('.scroll-statement__words span')];
    if (!this.words.length) return;
    this.classList.add('scroll-words-ready');
    let ticking = false;
    this.onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const rect = this.getBoundingClientRect();
        const vh = window.innerHeight;
        // Starts lighting when the text enters the lower part of the screen, done by mid-screen.
        const progress = Math.min(1, Math.max(0, (vh * 0.85 - rect.top) / (vh * 0.45 + rect.height * 0.6)));
        const lit = Math.round(progress * this.words.length);
        this.words.forEach((word, i) => word.classList.toggle('is-lit', i < lit));
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }
}
customElements.define('scroll-words', ScrollWords);

/* Rotating words ("Open it when you're feeling …") ------------------------ */

class WordRotator extends HTMLElement {
  connectedCallback() {
    this.words = [...this.querySelectorAll('.rotating-words__word')];
    if (this.words.length < 2 || reducedMotion.matches) return;
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 2000;
    // Only rotate while visible.
    this.observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.start() : this.stop()));
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stop() {
    clearInterval(this.timer);
  }

  next() {
    const current = this.words[this.index];
    current.classList.remove('is-active');
    current.classList.add('is-leaving');
    setTimeout(() => current.classList.remove('is-leaving'), 500);
    this.index = (this.index + 1) % this.words.length;
    this.words[this.index].classList.add('is-active');
  }
}
customElements.define('word-rotator', WordRotator);

/* Back to top --------------------------------------------------------------- */

class BackToTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    const toggle = () => this.button.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.9);
    window.addEventListener('scroll', debounce(toggle, 50), { passive: true });
    toggle();
    this.button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    });
  }
}
customElements.define('back-to-top', BackToTop);

/* Scroll reveal ------------------------------------------------------------- */

(() => {
  const root = document.documentElement;
  if (!root.classList.contains('reveal-enabled') || reducedMotion.matches || !('IntersectionObserver' in window)) {
    return;
  }
  // Hide-then-reveal only once this script is running, so content never stays hidden if JS fails.
  root.classList.add('reveal-ready');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  const observe = (scope = document) => scope.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));
  observe();
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
})();
